import {
  RADAR_FLIGHTS_STATUS_CODE_PATH,
  RADAR_STATUS_CODE_PATH,
  ResultStatus,
} from '@web-platform/shared-universal-constants/src/radar';
import {
  RADAR_CREATE_SESSIONS,
  RADAR_POLL,
} from '@web-platform/shared-universal-types/src/network/requestTypes';
import { isInArray } from '@web-platform/shared-universal-utils-universal/src/is-in-array/is-in-array';
import get from 'lodash/get';

import type {
  RadarResponsePayload,
  RadarResponsePayloadSuccess,
} from '@web-platform/shared-universal-types/src/network/endpoints/radar/radarResponsePayloadTypes';
import type {
  MutationType,
  QueryType,
} from '@web-platform/shared-universal-types/src/network/requestTypes';
import type { PayloadResponse } from '@web-platform/shared-universal-types/src/redux/actions/actions-shared-types';
import type { MutationItem } from '@web-platform/shared-universal-types/src/redux/state/network/mutations';
import type { Nullable } from '@web-platform/shared-universal-types-utils/src/utils';

export const isCompleteForRadarResponse = (response: Nullable<PayloadResponse>): boolean => {
  if (!response || response.error) return false;

  // Defensive fallback (carried over from Banana's original isRadarComplete): a radar
  // response with no status is treated as COMPLETE. `status` is a required field on the
  // type, so this only guards against malformed payloads.
  const status = get(response, RADAR_STATUS_CODE_PATH) || ResultStatus.COMPLETE;

  return isInArray(status, [ResultStatus.COMPLETE, ResultStatus.COMPLETE_NOT_MODIFIED]);
};

export const isRadarResponse = (
  requestType: QueryType | MutationType,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  response?: PayloadResponse,
): response is RadarResponsePayload => {
  const radarRequestTypes: Array<QueryType | MutationType> = [RADAR_POLL, RADAR_CREATE_SESSIONS];
  return radarRequestTypes.includes(requestType);
};

export const isRadarResponseSuccess = (
  requestType: QueryType | MutationType,
  response: PayloadResponse,
): response is RadarResponsePayloadSuccess => {
  if (!isRadarResponse(requestType, response)) return false;
  if (response.error || 'errors' in response.body) return false;
  if (
    get(response, RADAR_STATUS_CODE_PATH) === ResultStatus.FAILURE ||
    get(response, RADAR_FLIGHTS_STATUS_CODE_PATH) === ResultStatus.FAILURE
  )
    return false;

  return true;
};

const isRadarResponseEmptyWithNotModified = (response: RadarResponsePayloadSuccess) => {
  const status = get(response, RADAR_FLIGHTS_STATUS_CODE_PATH);
  const isNotModified = isInArray(status, [
    ResultStatus.COMPLETE_NOT_MODIFIED,
    ResultStatus.INCOMPLETE_NOT_MODIFIED,
  ]);

  const results = response.body.itineraries?.results || [];
  return isNotModified && results.length === 0;
};

export const isFlightsResponseSuccessAndValid = (
  requestType: QueryType | MutationType,
  response: PayloadResponse,
): response is RadarResponsePayloadSuccess =>
  isRadarResponseSuccess(requestType, response) && !isRadarResponseEmptyWithNotModified(response);

const isResponseFailed = (statusCode: string) => statusCode === ResultStatus.FAILURE || !statusCode;

export const isMutationItemResponseANonRetriableError = (
  mutation: MutationItem | undefined,
): boolean => {
  if (!mutation?.response) {
    return false;
  }

  if (mutation.type === RADAR_CREATE_SESSIONS && mutation.response.status === 200) {
    const flightsStatusCode = get(mutation.response, RADAR_FLIGHTS_STATUS_CODE_PATH, '');
    const rootStatusCode = get(mutation.response, RADAR_STATUS_CODE_PATH, '');

    return isResponseFailed(flightsStatusCode) || isResponseFailed(rootStatusCode);
  }

  return false;
};
